import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '../../components/Modal';
import {
  useCreateCompany,
  useUpdateCompany,
  useIndustries,
  useAccountTypes,
  useBusinessEntities,
  useTenantUsers,
  useSetCustomFieldValues,
} from '../hooks';
import { CustomFieldsForm } from '../CustomFieldsForm';
import type { Company, CreateCompanyPayload, CustomFieldValue } from '../types';

interface CompanyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  company?: Company | null;
  onSuccess: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text)',
  marginBottom: '0.25rem',
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: '2.5rem',
  padding: '0 0.75rem',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.9375rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--color-bg-card)',
  color: 'var(--color-text)',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.75rem center',
  paddingRight: '2.5rem',
};

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.75rem',
};

const fieldStyle: React.CSSProperties = {
  marginBottom: '0.875rem',
};

export function CompanyFormModal({
  isOpen,
  onClose,
  company,
  onSuccess,
}: CompanyFormModalProps) {
  const isEditMode = !!company;
  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();
  const setCustomFieldValues = useSetCustomFieldValues();
  const mutation = isEditMode ? updateMutation : createMutation;

  const customFieldValuesRef = useRef<Record<string, string | number | boolean | string[] | null>>({});
  const customFields: CustomFieldValue[] = company?.customFields ?? [];
  const handleCustomFieldsChange = useCallback(
    (values: Record<string, string | number | boolean | string[] | null>) => {
      customFieldValuesRef.current = values;
    },
    [],
  );

  // Dropdown data
  const { data: industriesData } = useIndustries();
  const { data: accountTypesData } = useAccountTypes();
  const { data: businessEntitiesData } = useBusinessEntities();
  const { data: usersData } = useTenantUsers();

  const industries = industriesData?.data ?? [];
  const accountTypes = accountTypesData?.data ?? [];
  const businessEntities = businessEntitiesData?.data ?? [];
  const tenantUsers = usersData?.data ?? [];

  // UI state
  const [activeTab, setActiveTab] = useState<'general' | 'details'>('general');
  const [showBillingEmail, setShowBillingEmail] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [error, setError] = useState('');

  // Form fields
  const [customerNumber, setCustomerNumber] = useState('');
  const [name, setName] = useState('');
  const [industryId, setIndustryId] = useState('');
  const [accountTypeId, setAccountTypeId] = useState('');
  const [businessEntityId, setBusinessEntityId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [street, setStreet] = useState('');
  const [zip, setZip] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('DE');
  const [notes, setNotes] = useState('');
  const [tradeRegisterNumber, setTradeRegisterNumber] = useState('');
  const [vatId, setVatId] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      setActiveTab('general');
      setShowBillingEmail(false);
      setBillingEmail('');
      if (company) {
        setCustomerNumber(company.customerNumber ?? '');
        setName(company.name);
        setIndustryId(company.industryId ?? '');
        setAccountTypeId(company.accountTypeId ?? '');
        setBusinessEntityId(company.businessEntityId ?? '');
        setOwnerId(company.ownerId ?? '');
        setEmail(company.email ?? '');
        setPhone(company.phone ?? '');
        setWebsite(company.website ?? '');
        setStreet(company.street ?? '');
        setZip(company.zip ?? '');
        setCity(company.city ?? '');
        setCountry(company.country ?? 'DE');
        setNotes(company.notes ?? '');
        setTradeRegisterNumber(company.tradeRegisterNumber ?? '');
        setVatId(company.vatId ?? '');
        setTagsInput(company.tags?.join(', ') ?? '');
      } else {
        setCustomerNumber('');
        setName('');
        setIndustryId('');
        setAccountTypeId('');
        setBusinessEntityId('');
        setOwnerId('');
        setEmail('');
        setPhone('');
        setWebsite('');
        setStreet('');
        setZip('');
        setCity('');
        setCountry('DE');
        setNotes('');
        setTradeRegisterNumber('');
        setVatId('');
        setTagsInput('');
      }
    }
  }, [isOpen, company]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name ist ein Pflichtfeld');
      return;
    }

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const selectedUser = ownerId
      ? tenantUsers.find((u) => u.id === ownerId)
      : null;
    const ownerName = selectedUser
      ? [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ') || selectedUser.email
      : undefined;

    const empty = isEditMode ? null : undefined;
    const trimOrEmpty = (v: string) => v.trim() || empty;

    const payload = {
      name: name.trim(),
      customerNumber: trimOrEmpty(customerNumber),
      industryId: industryId || empty,
      accountTypeId: accountTypeId || empty,
      businessEntityId: businessEntityId || empty,
      ownerId: ownerId || empty,
      ownerName: ownerName ?? empty,
      email: trimOrEmpty(email),
      phone: trimOrEmpty(phone),
      website: trimOrEmpty(website),
      street: trimOrEmpty(street),
      zip: trimOrEmpty(zip),
      city: trimOrEmpty(city),
      country: country || 'DE',
      notes: trimOrEmpty(notes),
      tradeRegisterNumber: trimOrEmpty(tradeRegisterNumber),
      vatId: trimOrEmpty(vatId),
      tags,
    };

    const saveCustomFields = (entityId: string) => {
      const vals = customFieldValuesRef.current;
      const entries = Object.entries(vals);
      if (entries.length === 0) return;
      setCustomFieldValues.mutate({
        entityId,
        data: {
          values: entries.map(([fieldDefId, value]) => ({ fieldDefId, value })),
        },
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const extractErrorMsg = (err: unknown, fallback: string): string => {
      const resp = (err as any)?.response?.data;
      if (resp?.message) {
        if (Array.isArray(resp.message)) return resp.message.join(', ');
        if (typeof resp.message === 'string') return resp.message;
      }
      if (resp?.error?.message) return resp.error.message;
      return fallback;
    };

    if (isEditMode && company) {
      updateMutation.mutate(
        { id: company.id, data: payload },
        {
          onSuccess: () => {
            saveCustomFields(company.id);
            onSuccess();
          },
          onError: (err: unknown) => {
            setError(extractErrorMsg(err, 'Fehler beim Speichern'));
          },
        },
      );
    } else {
      createMutation.mutate(payload as CreateCompanyPayload, {
        onSuccess: (res) => {
          if (res?.data?.id) saveCustomFields(res.data.id);
          onSuccess();
        },
        onError: (err: unknown) => {
          setError(extractErrorMsg(err, 'Fehler beim Anlegen'));
        },
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Unternehmen bearbeiten' : 'Neues Unternehmen'}
      maxWidth="680px"
    >
      <form onSubmit={handleSubmit}>

        {/* ── Modal info header with logo / initials ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.875rem',
          padding: '0.875rem 1rem',
          background: '#eff6ff',
          borderRadius: 'var(--radius-sm)',
          marginBottom: '1.25rem',
        }}>
          <div style={{
            width: 48, height: 48,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid #bfdbfe',
            background: company?.logoUrl ? 'white' : 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
            fontSize: '1.125rem', fontWeight: 700, color: 'white',
          }}>
            {company?.logoUrl ? (
              <img src={company.logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <span>{name?.[0]?.toUpperCase() || '?'}</span>
            )}
          </div>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e40af' }}>
              {name || (isEditMode ? company?.name : 'Neues Unternehmen') || 'Neues Unternehmen'}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#3b82f6' }}>
              {isEditMode ? 'Stammdaten bearbeiten' : 'Neues Unternehmen anlegen'}
            </div>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display: 'flex', borderBottom: '2px solid var(--color-border)', marginBottom: '1.25rem' }}>
          {(['general', 'details'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.5rem 1.25rem',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
                marginBottom: -2,
                background: 'transparent',
                fontSize: '0.875rem',
                fontWeight: activeTab === tab ? 600 : 400,
                color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-muted)',
                cursor: 'pointer',
              }}
            >
              {tab === 'general' ? 'Allgemein' : 'Details & Adresse'}
            </button>
          ))}
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div style={{
            padding: '0.75rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-error)',
            fontSize: '0.875rem',
            marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: Allgemein
            ══════════════════════════════════════════ */}
        {activeTab === 'general' && (
          <div>
            {/* Kundennummer + Zuständigkeit */}
            <div style={{ ...rowStyle, ...fieldStyle }}>
              <div>
                <label style={labelStyle}>Kundennummer</label>
                <input
                  style={inputStyle}
                  value={customerNumber}
                  onChange={(e) => setCustomerNumber(e.target.value)}
                  placeholder="z.B. KD-10001"
                />
              </div>
              <div>
                <label style={labelStyle}>Account Owner</label>
                <select
                  style={selectStyle}
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                >
                  <option value="">— Kein Account Owner —</option>
                  {tenantUsers.slice().sort((a, b) => {
                    const nameA = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email;
                    const nameB = [b.firstName, b.lastName].filter(Boolean).join(' ') || b.email;
                    return nameA.localeCompare(nameB, 'de');
                  }).map((u) => {
                    const label = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
                    return (
                      <option key={u.id} value={u.id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* Name */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Name *</label>
              <input
                style={inputStyle}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Firmenname"
                required
              />
            </div>

            {/* Kontotyp + Branche */}
            <div style={{ ...rowStyle, ...fieldStyle }}>
              <div>
                <label style={labelStyle}>Kontotyp</label>
                <select
                  style={selectStyle}
                  value={accountTypeId}
                  onChange={(e) => setAccountTypeId(e.target.value)}
                >
                  <option value="">— Keine —</option>
                  {accountTypes.slice().sort((a, b) => a.name.localeCompare(b.name, 'de')).map((at) => (
                    <option key={at.id} value={at.id}>{at.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Branche</label>
                <select
                  style={selectStyle}
                  value={industryId}
                  onChange={(e) => setIndustryId(e.target.value)}
                >
                  <option value="">— Keine —</option>
                  {industries.slice().sort((a, b) => a.name.localeCompare(b.name, 'de')).map((ind) => (
                    <option key={ind.id} value={ind.id}>{ind.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Gesellschaft */}
            {businessEntities.length > 0 && (
              <div style={fieldStyle}>
                <label style={labelStyle}>Gesellschaft</label>
                <select
                  style={selectStyle}
                  value={businessEntityId}
                  onChange={(e) => setBusinessEntityId(e.target.value)}
                >
                  <option value="">— Keine Zuordnung —</option>
                  {businessEntities.slice().sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'de')).map((be) => (
                    <option key={be.id} value={be.id}>{be.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* E-Mail (Zentrale) + Rechnungs-E-Mail toggle */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Zentrale E-Mail</label>
              <input
                type="email"
                style={inputStyle}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@firma.de"
              />
              {!showBillingEmail ? (
                <button
                  type="button"
                  onClick={() => setShowBillingEmail(true)}
                  style={{
                    background: 'none', border: 'none', padding: '0.25rem 0',
                    fontSize: '0.8125rem', color: 'var(--color-primary)',
                    cursor: 'pointer', display: 'block', marginTop: '0.25rem',
                  }}
                >
                  + Rechnungs-E-Mail hinzufügen
                </button>
              ) : (
                <div style={{ marginTop: '0.5rem' }}>
                  <label style={labelStyle}>Rechnungs-E-Mail</label>
                  <input
                    type="email"
                    style={inputStyle}
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    placeholder="rechnung@firma.de"
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Telefon */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Telefon</label>
              <input
                style={inputStyle}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+49 ..."
              />
            </div>

            {/* Website */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Website</label>
              <input
                style={inputStyle}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://..."
              />
            </div>

            {/* Notizen */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Notizen</label>
              <textarea
                style={{ ...inputStyle, height: 'auto', minHeight: 60, padding: '0.625rem 0.75rem', resize: 'vertical' }}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Tags */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Tags (kommasepariert)</label>
              <input
                style={inputStyle}
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="VIP, Bestandskunde, ..."
              />
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: Details & Adresse
            ══════════════════════════════════════════ */}
        {activeTab === 'details' && (
          <div>
            {/* Straße */}
            <div style={fieldStyle}>
              <label style={labelStyle}>Straße</label>
              <input
                style={inputStyle}
                value={street}
                onChange={(e) => setStreet(e.target.value)}
              />
            </div>

            {/* Land + PLZ + Stadt */}
            <div style={{ display: 'grid', gridTemplateColumns: '60px 80px 1fr', gap: '0.5rem', ...fieldStyle }}>
              <div>
                <label style={labelStyle}>Land</label>
                <input
                  style={inputStyle}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  maxLength={2}
                  placeholder="DE"
                />
              </div>
              <div>
                <label style={labelStyle}>PLZ</label>
                <input
                  style={inputStyle}
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Stadt</label>
                <input
                  style={inputStyle}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
            </div>

            {/* HRB Nummer + UstID */}
            <div style={{ ...rowStyle, ...fieldStyle }}>
              <div>
                <label style={labelStyle}>HRB Nummer</label>
                <input
                  style={inputStyle}
                  value={tradeRegisterNumber}
                  onChange={(e) => setTradeRegisterNumber(e.target.value)}
                  placeholder="HRB 12345"
                />
              </div>
              <div>
                <label style={labelStyle}>UstID</label>
                <input
                  style={inputStyle}
                  value={vatId}
                  onChange={(e) => setVatId(e.target.value)}
                  placeholder="DE123456789"
                />
              </div>
            </div>

            {/* Custom Fields */}
            {customFields.length > 0 && (
              <div style={fieldStyle}>
                <CustomFieldsForm
                  fields={customFields}
                  onChange={handleCustomFieldsChange}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Action buttons — always visible ── */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--color-border)',
          marginTop: '0.5rem',
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: mutation.isPending ? 'wait' : 'pointer',
              opacity: mutation.isPending ? 0.7 : 1,
            }}
          >
            {mutation.isPending
              ? 'Speichern...'
              : isEditMode
                ? 'Speichern'
                : 'Anlegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
